import React, { useMemo, useState } from 'react'
import { Leader, User } from '../../store/types'
import { Autocomplete, MenuItem, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { StyledContainerColumnLeft, StyledContainerRowBetween } from '../../styles/ContainerStyles'
import { useGetUsersQuery } from '../../store/redux/reducers/usersSlice'
import AvatarComponent from '../avatar/AvatarComponent'

type SelectEmployeeProps = {
  employees: User[]
  replace: (users: User[]) => void
  leaders: Leader[] | undefined
}

const checkEmployeeExistence = (array: User[] | Leader[], index: string) =>
  array.map(employee => employee.id).includes(index)

const SelectEmployee = ({ employees, replace, leaders }: SelectEmployeeProps) => {
  const { data: response } = useGetUsersQuery({ page: 0, size: 500, employeeStatus: 'ACTIVE' })

  let users: User[] = useMemo(() => {
    if (response) return response.content
    return []
  }, [response])

  const options = useMemo(() => {
    return users.filter(
      entry => !checkEmployeeExistence(employees, entry.id) && !checkEmployeeExistence(leaders ?? [], entry.id)
    )
  }, [users, employees, leaders])

  const [selected] = useState<User[]>([])

  const { t } = useTranslation()

  return (
    <StyledContainerRowBetween sx={{ gap: 2 }}>
      <Autocomplete
        value={selected}
        options={options}
        multiple
        disableCloseOnSelect
        onChange={(_, value) => {
          replace([...value, ...employees])
        }}
        fullWidth
        disablePortal
        id="combo-box-all-employees"
        getOptionLabel={employee => employee.firstName + ' ' + employee.lastName}
        noOptionsText={t('components.autocomplete.projectTeam.employeesNotFound')}
        renderInput={params => (
          <TextField
            id={'combo-box-all-employees'}
            InputProps={params.InputProps}
            inputProps={{ ...params.inputProps, 'aria-label': t('components.autocomplete.projectTeam.label') }}
            fullWidth={true}
            size="small"
            label={t('components.autocomplete.projectTeam.label')}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props
          return (
            <MenuItem key={key} {...otherProps} sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
              <AvatarComponent
                extension={option.extension}
                avatar={option.avatar}
                alt={`${option.firstName}-${option.lastName}-profile-picture`}
                size={28}
              />

              <StyledContainerColumnLeft>
                <Typography variant="body2" sx={{ color: 'text.highEmphasis' }}>
                  {`${option.firstName} ${option.lastName}`}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.base' }}>
                  {option.email}
                </Typography>
              </StyledContainerColumnLeft>
            </MenuItem>
          )
        }}
        renderTags={() => null}
      />
    </StyledContainerRowBetween>
  )
}

export default SelectEmployee
